import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { translate } from '../../Services/translation';
import { getColor } from '../../styling/mixins/colors';
import { fontFamilyBold, text } from '../../styling/mixins/typography';
import { Price, ProductPagePriceProps } from './Product.Price.Models';

const ProductPagePrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .price {
    ${text('xlarge')}
    ${fontFamilyBold}
  }

  .campaignPrice {
    ${text('xlarge')}
    ${fontFamilyBold}
        color: ${getColor('primary')};
  }

  .originalPrice {
    ${text('large')}
    color: ${getColor('grey', 500)};
    margin-left: 0.4rem;
  }

  .withLineThrough {
    text-decoration: line-through;
  }
`;

const ProductPageVariantList = (props: ProductPagePriceProps) => {
  const { articleNumber } = useTypedSelector((state) => state.buyButton);
  const userInfo = useTypedSelector((state) => state.userInfo);
  const isBusinessCustomer = userInfo?.current?.isBusinessCustomer;

  const price = useMemo((): Price | undefined => {
    if (articleNumber != null && articleNumber != '' && props.prices) {
      return props.prices.find((x) => x.articleNumber == articleNumber);
    } else if (
      props.initialSelectVariantId &&
      props.initialSelectVariantId != ''
    ) {
      return props.prices.find(
        (x) => x.articleNumber == props.initialSelectVariantId
      );
    } else {
      return props.prices[0];
    }
  }, [articleNumber]);

  const isCampaignPrice = (price: Price | null | undefined) => {
    if (price) return price.campaignPrice ? true : false;
    else return false;
  };

  const getFormattedPrice = () => {
    if (price == null) {
      return getFromPrice();
    } else if (isCampaignPrice(price)) {
      return isBusinessCustomer
        ? price.formattedPriceWithoutVat
        : price.formattedCampaignPrice;
    } else {
      return isBusinessCustomer
        ? price.formattedPriceWithoutVat
        : price.formattedPrice;
    }
  };

  const getOriginalPrice = () => {
    if (price && isCampaignPrice(price)) return price.formattedPrice;
  };

  const getFromPrice = () => {
    return props.fromPrice.campaignPrice ? (
      <>
        <span className="campaignPrice">
          <span>{translate('product-page.from-product-price')}</span>{' '}
          {!isBusinessCustomer && props.fromPrice.formattedCampaignPrice}{' '}
          {isBusinessCustomer && props.fromPrice.formattedPriceWithoutVat}{' '}
        </span>{' '}
        <span className="originalPrice">
          {props.fromPrice.formattedPrice}
        </span>
      </>
    ) : (
      <span>
        <span>{translate('product-page.from-product-price')}</span>{' '}
        {!isBusinessCustomer && props.fromPrice.formattedPrice}{' '}
        {isBusinessCustomer && props.fromPrice.formattedPriceWithoutVat}{' '}
      </span>
    );
  };

  const getFromPriceDecimalString = () => {
    return props.fromPrice.campaignPrice
      ? props.fromPrice.campaignPrice.toString()
      : props.fromPrice.price.toString();
  };

  return (
    <ProductPagePrice>
      <span className={isCampaignPrice(price) ? 'campaignPrice' : 'price'}>
        {getFormattedPrice()}
      </span>

      {price != null && isCampaignPrice(price) ? (
        <span>
          <span className="originalPrice">
            {translate('product-page.from-original-price')}{' '}
          </span>
          <span className="originalPrice withLineThrough">
            {getOriginalPrice()}
          </span>
        </span>
      ) : (
        ''
      )}

      {isBusinessCustomer && (
        <div className="sales-price">
          <span>{price?.recommendedSalesPrice}</span>
        </div>
      )}

      <meta
        itemProp="price"
        content={price?.roundedPriceWithoutCurrency}
      />
      <meta itemProp="priceCurrency" content={props.currencyCode} />
    </ProductPagePrice>
  );
};

export default ProductPageVariantList;
